/**
 * Utility for managing canonical URLs consistently across the site
 */

const BASE_URL = 'https://ad-email-lab.com';

/**
 * Generates a clean canonical URL for the current page
 * Removes query parameters and hash fragments to avoid duplicate content issues
 */
export const getCanonicalUrl = (path?: string): string => {
  if (path) {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${BASE_URL}/${cleanPath}`;
  }
  
  // Get current path without query params or hash
  const currentPath = window.location.pathname;
  return `${BASE_URL}${currentPath}`;
};

/**
 * Sets the canonical URL for the current page
 * Creates or updates the canonical link element in the document head
 */
export const setCanonicalUrl = (path?: string): void => {
  const canonicalUrl = getCanonicalUrl(path);
  
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', canonicalUrl);
};

/**
 * Updates or creates a meta tag with the specified name and content
 */
export const updateMeta = (name: string, content: string): void => {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

/**
 * Updates or creates a meta tag with the specified property (used for OpenGraph tags)
 */
export const updateMetaProperty = (property: string, content: string): void => {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

/**
 * Sets per-page OpenGraph + Twitter social tags.
 */
export const setSocialTags = (opts: {
  title: string;
  description: string;
  path?: string;
  type?: 'website' | 'article';
  image?: string;
}): void => {
  const url = getCanonicalUrl(opts.path);
  const image = opts.image ?? `${BASE_URL}/lovable-uploads/9660a1c7-e321-4122-b380-58c44ed97180.png`;
  updateMetaProperty('og:title', opts.title);
  updateMetaProperty('og:description', opts.description);
  updateMetaProperty('og:url', url);
  updateMetaProperty('og:type', opts.type ?? 'website');
  updateMetaProperty('og:image', image);
  updateMeta('twitter:card', 'summary_large_image');
  updateMeta('twitter:title', opts.title);
  updateMeta('twitter:description', opts.description);
  updateMeta('twitter:image', image);
};

/**
 * Injects (or replaces) a JSON-LD <script> with a stable id.
 */
export const setJsonLd = (id: string, data: Record<string, unknown> | Record<string, unknown>[]): void => {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};